import { useState } from 'react';
import {
  Form,
  Input,
  Select,
  Tag,
  Button,
  Checkbox,
  Flex,
  Card,
  Upload,
  Image,
  Skeleton
} from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons/lib';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useErrorMessage } from '../../../../utils/errorMessage';
import { useFetchEnums } from '../../../../utils/useFetchEnums';
import { useCountryArray } from '../../../../utils/countryArray';
import { compressFile } from '../../../../utils/filesManagement/compressFile';
import { useCountryCodeInfos } from '../utils/useCountryCodeInfos';
import { countryCallingCodeOptions } from '../../../../utils/countryCodes';
import { createPhoneNumberFieldConfig } from '../../../../utils/formFields/phoneNumberField';

const { Option } = Select;

/**
 * Hook to manage the general fields in a form. It maintains the state of various fields and fetches necessary enums from the API.
 * It returns an object containing the basicFields (a structured representation of form fields) and a loading state for select options.
 *
 * @param {Object} form - Instance of form.
 *
 * @returns {Object} - An object containing basicFields array and isFieldsLoading boolean
 * @returns {Object[]} basicFields - An array of objects representing the structure and state of each form field
 * @returns {boolean} isFieldsLoading - A boolean indicating if select options are currently loading
 */
export const useGeneralFields = ({
  form,
  internetAccess,
  setInternetAccess,
  setPhotoFileList,
  photoFileList,
  photoID,
  loading,
  country,
  setCountry
}) => {
  const { message } = useErrorMessage();
  const { t } = useTranslation();
  const countries = useCountryArray();
  const { dispatchAPI, token } = useAuthContext();
  const { handleCountryCodeInfos } = useCountryCodeInfos(country);
  const { enums, isFieldsLoading } = useFetchEnums('/owners/enums', {
    dispatchAPI,
    message
  });

  const [cityOptions, setCityOptions] = useState([]);

  const filterOption = (input, option) =>
    option.props.value.toLowerCase().includes(input.toLowerCase());

  const draggerProps = {
    onRemove: (file) => {
      const index = photoFileList.indexOf(file);
      const newFileList = photoFileList.slice();
      newFileList.splice(index, 1);
      setPhotoFileList(newFileList);
    },
    beforeUpload: async (file) => {
      const newFile = await compressFile(file);
      setPhotoFileList([newFile]);
      return false;
    },
    fileList: photoFileList || []
  };

  const basicFields = [
    {
      label: 'photo',
      input: (
        <Upload {...draggerProps} listType="picture-circle">
          <Skeleton loading={loading} avatar active>
            {photoID ? (
              <Image
                src={`${process.env.REACT_APP_API_URL}/get-file-url/${photoID}/${token}`}
                alt="avatar"
              />
            ) : (
              '+ Upload'
            )}
          </Skeleton>
        </Upload>
      )
    },
    {
      name: ['civility'],
      rules: [{ required: true }],
      input: (
        <Select loading={isFieldsLoading}>
          {(enums?.civility || []).map((civility) => (
            <Option key={civility} value={civility}>
              <Tag>{t(`owners.tags.${civility}`)}</Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      name: ['last_name'],
      rules: [{ required: true }]
    },
    {
      name: ['first_name'],
      rules: [{ required: true }]
    },
    createPhoneNumberFieldConfig('cell_phone_number'),
    createPhoneNumberFieldConfig('phone_number'),
    {
      noLabel: true,
      input: (
        <Form.List name={['other_phone_number']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ name, key }) => (
                <Card key={key} className="other-phone-number-card">
                  <Flex justify="space-between" align="center">
                    {`${t('owners.form.other_phone_number.title')} ${name + 1}`}
                    <Button type="link" onClick={() => remove(name)}>
                      <CloseOutlined />
                    </Button>
                  </Flex>

                  <Input.Group compact>
                    <Form.Item
                      noStyle
                      name={[name, 'country_code']}
                      initialValue="+33"
                    >
                      <Select
                        style={{ width: 90 }}
                        options={countryCallingCodeOptions}
                      />
                    </Form.Item>
                    <Form.Item noStyle name={[name, 'number']}>
                      <Input
                        type="number"
                        style={{ width: 'calc(100% - 90px)' }}
                      />
                    </Form.Item>
                  </Input.Group>
                </Card>
              ))}
              <Button
                type="dashed"
                block
                onClick={() => add()}
                icon={<PlusOutlined />}
              >
                {t('owners.form.other_phone_number.add_button')}
              </Button>
            </>
          )}
        </Form.List>
      )
    },
    {
      name: ['internet_access'],
      initialValue: internetAccess,
      input: (
        <Checkbox
          defaultChecked={internetAccess}
          onChange={() => {
            if (!internetAccess) form.setFieldValue('email', null);
            setInternetAccess(!internetAccess);
          }}
        />
      ),
      valuePropName: 'checked'
    },
    ...(internetAccess
      ? [
          {
            name: ['email'],
            rules: [{ required: true }, { type: 'email' }]
          }
        ]
      : []),
    {
      name: ['first_profession']
    },
    {
      name: ['second_profession']
    },
    {
      label: 'address.number',
      name: ['address', 'number'],
      rules: [{ required: true }]
    },
    {
      label: 'address.street',
      name: ['address', 'street'],
      rules: [{ required: true }]
    },
    {
      label: 'address.additional',
      name: ['address', 'additional']
    },
    {
      label: 'address.country',
      name: ['address', 'country'],
      input: (
        <Select
          showSearch
          loading={isFieldsLoading}
          filterOption={filterOption}
          onChange={(value) => {
            setCountry(value);
            form.setFieldValue(['address', 'postal_code'], undefined);
            form.setFieldValue(['address', 'city'], undefined);
            form.setFieldValue(['address', 'state'], undefined);
            setCityOptions([]);
          }}
        >
          {(countries || []).map((value) => (
            <Option key={value} value={value}>
              <Tag>{value}</Tag>
            </Option>
          ))}
        </Select>
      ),
      rules: [{ required: true }]
    },
    {
      label: 'address.postal_code',
      name: ['address', 'postal_code'],
      input: (
        <Input
          onChange={(e) => {
            const postalCode = e.target.value;

            form.setFieldValue(['address', 'city'], undefined);
            form.setFieldValue(['address', 'state'], undefined);
            setCityOptions([]);

            if (postalCode.length < 5) return;

            handleCountryCodeInfos(postalCode).then(
              ({ cities, city, state }) => {
                form.setFieldValue(['address', 'state'], state || undefined);

                if (cities.length > 1) {
                  setCityOptions(cities);
                } else {
                  form.setFieldValue(['address', 'city'], city || undefined);
                  setCityOptions([]);
                }
              }
            );
          }}
        />
      ),
      dependencies: ['address', 'country'],
      rules: [{ required: true }]
    },
    {
      label: 'address.department',
      name: ['address', 'state'],
      rules: [{ required: true }]
    },
    {
      label: 'address.city',
      name: ['address', 'city'],
      rules: [{ required: true }],
      input:
        cityOptions.length > 1 ? (
          <Select
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {cityOptions.map((city) => (
              <Option key={city} value={city}>
                {city}
              </Option>
            ))}
          </Select>
        ) : (
          <Input />
        )
    }
  ];

  return {
    basicFields,
    isFieldsLoading
  };
};
